// @flow

import { isEqual } from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import queryString from "query-string";
import NProgress from "nprogress";

import API from "../api";
import addACFData from "../utils/addACFData";
import GTM from "../utils/gtm";
import getContentReferenceTileProps from "../utils/getContentReferenceTileProps";
import useAsyncState from "../hooks/useAsyncState";

import HeroSearch from "../components/organisms/HeroSearch";
import Tile from "../components/molecules/Tile";
import Pagination from "../components/molecules/Pagination";
import PageTotals from "../components/atoms/PageTotals";
import FiltersModal from "../components/organisms/FiltersModal";
import Button from "../components/atoms/Button";
import CustomMeta from "../components/atoms/CustomMeta";

type PropsType = {};

const SearchPage = (props: PropsType): any => {
    const POSTS_PER_PAGE = 12;
    const PAGINATION_WIDTH = 2;

    const history = useHistory();
    const [state, actions] = useAsyncState();
    const [params, setParams] = useState({ page: 1 });
    const [posts, setPosts] = React.useState([]);
    const [headers, setHeaders] = React.useState({});
    // const [initialLoad, setInitialLoad] = React.useState(true);
    const [activeFilterModal, setActiveFilterModal] = React.useState(false);

    const queryParamProps = queryString.parse(history.location.search);
    const activePage = Number(queryParamProps.page_num) || 1;
    const totalPages = parseInt(headers["x-wp-totalpages"]) || 1;
    const totalItems = parseInt(headers["x-wp-total"]) || 0;
    const searchFilters = [
        {
            label: "Type",
            option_key: "type",
            option_type: "single",
            options: [
                {
                    default: false,
                    label: "Clubs",
                    key: "club",
                },
                {
                    default: false,
                    label: "Events",
                    key: "event",
                },
                {
                    default: false,
                    label: "Products",
                    key: "product",
                },
                {
                    default: false,
                    label: "Page",
                    key: "page",
                },
            ],
        },
    ];

    // set query params in state on change of any params
    if (!isEqual(queryParamProps, params)) {
        setParams(queryParamProps);
    }

    // Whenever the search term changes
    React.useEffect(() => {
        // only search if valid search q value
        if (params.q) {
            actions.request();
            NProgress.start();

            API.search({
                term: params.q,
                page: params.page_num,
                type: params.type,
                perPage: POSTS_PER_PAGE,
                fullMode: true,
            })
                .then(
                    ({
                        data,
                        headers,
                    }: {
                        data: Array<any>,
                        headers: Object,
                    }) => {
                        setPosts(
                            data.search_result
                                ? data.search_result.map((result: any) => {
                                      return {
                                          ...result,
                                          ...data.related[result.ID],
                                      };
                                  })
                                : []
                        );
                        setHeaders(headers);
                    }
                )
                .then(actions.success)
                .catch(actions.fail)
                .finally(() => {
                    // setInitialLoad(false);
                    NProgress.done();
                    scrollToTop();

                    if (params.q) {
                        // log GTM search event
                        GTM.push({
                            event: "site_search",
                            search_term: params.q,
                            page_type: `${window.location.pathname}`,
                        });
                    }
                });
        }
    }, [params]); // eslint-disable-line

    const scrollToTop = () => {
        // if (containerRef.current) {
        //     scrollToEl(containerRef.current);
        // }

        window.scrollTo({
            top: 0,
            left: 0,
            // behavior: "smooth",
        });
    };

    const getTotalFiltersApplied = (): number => {
        let filtersApplied = 0;

        let filterKeys = {
            ...queryParamProps,
        };

        // dont include page or search query as part of count
        delete filterKeys.page;
        delete filterKeys.page_num;
        delete filterKeys.search;
        delete filterKeys.q;

        Object.keys(filterKeys).forEach((paramKey: string): any => {
            if (filterKeys[paramKey]) {
                filtersApplied =
                    filtersApplied + filterKeys[paramKey].split(",").length;
            }
        });
        return filtersApplied;
    };

    const renderPost = (
        post: any,
        featured: boolean,
        index: number
    ): React.Node => {
        const mappedTileProps = post.acf
            ? getContentReferenceTileProps(post)
            : post.tile_reference
            ? getContentReferenceTileProps(addACFData(post.tile_reference))
            : null;

        return (
            <div key={index} className="listing__item-row">
                <Tile {...mappedTileProps} expanded={true} />
            </div>
        );
    };

    return (
        <div className={`listing ${state.loading ? "is-loading" : ""}`}>
            <CustomMeta
                title="Search"
                description="Search for content across Activate UTS"
            />

            <HeroSearch />

            <div className="listing__inner">
                <div className="listing__container container-fluid">
                    <div className="container-fluid--medium">
                        {/* No results found */}
                        {!state.loading && posts && posts.length < 1 ? (
                            <>
                                <br />
                                <br />
                                No results found for "{queryParamProps.q}".
                            </>
                        ) : null}
                        {/* Listing items */}
                        {posts && posts.length ? (
                            <>
                                <div className="listing__controls">
                                    <Button
                                        className="listing__filter-btn"
                                        inline={true}
                                        onClick={() =>
                                            setActiveFilterModal(true)
                                        }
                                        iconBefore="filter"
                                        counter={
                                            getTotalFiltersApplied() &&
                                            getTotalFiltersApplied() > 0
                                                ? getTotalFiltersApplied()
                                                : null
                                        }
                                    >
                                        Filter
                                    </Button>
                                    <FiltersModal
                                        heading={"Filter"}
                                        active={activeFilterModal}
                                        filters={searchFilters}
                                        onClose={() =>
                                            setActiveFilterModal(false)
                                        }
                                    />
                                </div>

                                <div className="listing__totals">
                                    <PageTotals
                                        currentPageNumber={activePage}
                                        postsPerPage={POSTS_PER_PAGE}
                                        totalPosts={totalItems}
                                    />
                                </div>

                                <div className="listing__items">
                                    {posts.map(
                                        (post: any, index: number): any => {
                                            return renderPost(
                                                post,
                                                false,
                                                index
                                            );
                                        }
                                    )}
                                </div>

                                {/* Listing pagination */}
                                <div className="listing__pagination">
                                    {totalPages > 1 ? (
                                        <Pagination
                                            ellipsisWidth={PAGINATION_WIDTH}
                                            pages={totalPages}
                                            activePage={activePage}
                                        />
                                    ) : null}
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchPage;
